import type { BuildingKind, ItemKind } from './types';

export interface ItemDef {
  name: string;
  color: string;
  glyph: 'diamond' | 'circle' | 'square' | 'hex' | 'tri' | 'star';
}

export const ITEMS: Record<ItemKind, ItemDef> = {
  request: { name: 'Request', color: '#F2B84B', glyph: 'circle' },
  context: { name: 'Context', color: '#3CC8B4', glyph: 'diamond' },
  spec: { name: 'Spec', color: '#6DA8FF', glyph: 'square' },
  draft: { name: 'Draft', color: '#A98BFF', glyph: 'hex' },
  build: { name: 'Build', color: '#59D98E', glyph: 'star' },
  bug: { name: 'Bug', color: '#FF6B6B', glyph: 'tri' },
};

export type Category = 'logistics' | 'production' | 'safety' | 'compute' | 'automation' | 'map';

export interface BuildingDef {
  kind: BuildingKind;
  name: string;
  /** One line, plain language, shown in the toolbar tooltip. */
  blurb: string;
  w: number;
  h: number;
  cost: number;
  category: Category;
  hotkey?: string;
  /** Compute drawn while working. */
  compute: number;
  craftTime: number;
  hp: number;
  placeable: boolean;
  color: string;
}

export const BUILDINGS: Record<BuildingKind, BuildingDef> = {
  belt: {
    kind: 'belt', name: 'Belt', blurb: 'Moves work in the direction it points. Drag to lay a line.',
    w: 1, h: 1, cost: 1, category: 'logistics', hotkey: '1', compute: 0, craftTime: 0, hp: 40, placeable: true, color: '#243142',
  },
  indexer: {
    kind: 'indexer', name: 'Indexer', blurb: 'Pulls requests or context out of the ground it sits on.',
    w: 2, h: 2, cost: 10, category: 'production', hotkey: '2', compute: 0, craftTime: 1.5, hp: 80, placeable: true, color: '#2B3A4D',
  },
  specdesk: {
    kind: 'specdesk', name: 'Spec Desk', blurb: 'Request + context becomes a spec.',
    w: 2, h: 2, cost: 16, category: 'production', hotkey: '3', compute: 0, craftTime: 1.2, hp: 80, placeable: true, color: '#22385A',
  },
  forge: {
    kind: 'forge', name: 'Agent Forge', blurb: 'An agent turns a spec into a draft. Fast, needs compute, sometimes wrong.',
    w: 2, h: 2, cost: 22, category: 'production', hotkey: '4', compute: 3, craftTime: 1.0, hp: 80, placeable: true, color: '#33285A',
  },
  bench: {
    kind: 'bench', name: 'Test Bench', blurb: 'Checks drafts. Good ones become builds; bad ones leave by the red side as bugs.',
    w: 2, h: 2, cost: 18, category: 'safety', hotkey: '5', compute: 1, craftTime: 2.0, hp: 80, placeable: true, color: '#1F4A39',
  },
  postmortem: {
    kind: 'postmortem', name: 'Postmortem Lab', blurb: 'Turns bugs into insight for research and soaks up nearby tech debt.',
    w: 2, h: 2, cost: 14, category: 'safety', hotkey: '6', compute: 0, craftTime: 2.5, hp: 80, placeable: true, color: '#4A2A2E',
  },
  router: {
    kind: 'router', name: 'Router', blurb: 'Splits a line: sends items out every other side in turn.',
    w: 1, h: 1, cost: 4, category: 'logistics', hotkey: '7', compute: 0, craftTime: 0, hp: 40, placeable: true, color: '#2A3A4A',
  },
  rack: {
    kind: 'rack', name: 'Compute Rack', blurb: 'Powers forges, benches and monitors nearby. Costs a little every second.',
    w: 2, h: 2, cost: 26, category: 'compute', hotkey: '8', compute: 0, craftTime: 0, hp: 100, placeable: true, color: '#4A3F1E',
  },
  breaker: {
    kind: 'breaker', name: 'Circuit Breaker', blurb: 'A belt that trips when corrupted work hits it, so damage stops here.',
    w: 1, h: 1, cost: 6, category: 'safety', hotkey: '9', compute: 0, craftTime: 0, hp: 60, placeable: true, color: '#3B2F18',
  },
  gate: {
    kind: 'gate', name: 'Version Gate', blurb: 'A belt that checks each change against the latest version of the record it edits. Stale changes stop here instead of shipping.',
    w: 1, h: 1, cost: 6, category: 'safety', hotkey: 'K', compute: 0, craftTime: 0, hp: 60, placeable: true, color: '#1B3A44',
  },
  monitor: {
    kind: 'monitor', name: 'Monitor', blurb: 'Zaps glitches in range and chips away at nests. Uses compute while firing.',
    w: 2, h: 2, cost: 24, category: 'safety', hotkey: '0', compute: 1, craftTime: 0, hp: 120, placeable: true, color: '#1E3F4A',
  },
  firewall: {
    kind: 'firewall', name: 'Firewall', blurb: 'A wall glitches have to chew through.',
    w: 1, h: 1, cost: 2, category: 'safety', hotkey: 'F', compute: 0, craftTime: 0, hp: 160, placeable: true, color: '#3A3F46',
  },
  checkpoint: {
    kind: 'checkpoint', name: 'Checkpoint', blurb: 'A restore point. Roll back everything nearby, and act boldly inside its circle.',
    w: 2, h: 2, cost: 30, category: 'safety', hotkey: 'C', compute: 0, craftTime: 0, hp: 100, placeable: true, color: '#1E3A5A',
  },
  scanner: {
    kind: 'scanner', name: 'Scanner', blurb: 'Clears the fog in a wide circle.',
    w: 2, h: 2, cost: 20, category: 'automation', hotkey: 'N', compute: 0, craftTime: 0, hp: 80, placeable: true, color: '#2A2F4A',
  },
  drones: {
    kind: 'drones', name: 'Drone Hub', blurb: 'Drones build your blueprint ghosts and patch corrupted machines in range.',
    w: 2, h: 2, cost: 50, category: 'automation', hotkey: 'G', compute: 0, craftTime: 0, hp: 120, placeable: true, color: '#3A2A4A',
  },
  users: {
    kind: 'users', name: 'Users', blurb: 'Ship drafts or builds here. Bad work hurts trust.',
    w: 3, h: 3, cost: 0, category: 'map', compute: 0, craftTime: 0, hp: 9999, placeable: false, color: '#1D3B2E',
  },
  terminal: {
    kind: 'terminal', name: 'Operator Terminal', blurb: 'An old console with a line to the whole factory.',
    w: 2, h: 2, cost: 0, category: 'map', compute: 0, craftTime: 0, hp: 9999, placeable: false, color: '#203040',
  },
  monolith: {
    kind: 'monolith', name: 'Legacy Monolith', blurb: 'Owned by the Platform team. Do not touch. Full of context.',
    w: 6, h: 6, cost: 0, category: 'map', compute: 0, craftTime: 0, hp: 9999, placeable: false, color: '#2A2530',
  },
  nest: {
    kind: 'nest', name: 'Glitch Nest', blurb: 'Feeds on tech debt and sends glitches at your machines.',
    w: 3, h: 3, cost: 0, category: 'map', compute: 0, craftTime: 0, hp: 600, placeable: false, color: '#3A1530',
  },
  cache: {
    kind: 'cache', name: 'Forgotten Budget', blurb: 'An unclaimed budget line. Click to claim it.',
    w: 2, h: 2, cost: 0, category: 'map', compute: 0, craftTime: 0, hp: 9999, placeable: false, color: '#3A3418',
  },
};

export const TOOLBAR: BuildingKind[] = [
  'belt', 'indexer', 'specdesk', 'forge', 'bench', 'postmortem', 'router', 'rack',
  'breaker', 'gate', 'monitor', 'firewall', 'checkpoint', 'scanner', 'drones',
];

// Tuning constants. Balance tests in tests/balance.test.ts lean on these.
export const TUNING = {
  beltSpeed: 2.0, // tiles per second
  beltGap: 0.5, // two items per tile
  queueCap: 2,
  rackSupply: 8,
  rackRadius: 6.5,
  rackUpkeep: 0.25, // credits per second per rack
  forgeHallucination: 0.3,
  promptOnlyPenalty: 0.2, // extra hallucination when a forge drafts straight from a request
  benchCatch: 0.92,
  releaseValue: { draft: 3, build: 6 } as Record<string, number>,
  flawedTrustLoss: 5,
  corruptTrustLoss: 9,
  glitchTrustLoss: 10,
  slaPenalty: 6,
  trustRegen: 0.35,
  debtPerDraft: 0.9,
  debtPromptOnly: 1.6,
  debtDecay: 0.012,
  debtDiffuse: 0.05,
  postmortemAbsorb: 0.8,
  nestAbsorb: 1.6,
  glitchCost: 14, // debt a nest needs per glitch
  glitchHp: 30,
  glitchSpeed: 1.35,
  glitchAttackTime: 2.0,
  firewallDps: 30,
  monitorRadius: 7.5,
  monitorDps: 34,
  nestDps: 10,
  corruptEmitEvery: 2.4,
  patchCost: 8,
  checkpointRadius: 8.5,
  checkpointCooldown: 40,
  scannerRadius: 16,
  revealRadius: 5,
  droneRange: 14,
  dronesPerHub: 4,
  droneSpeed: 9,
  breakerAutoReset: 3,
  /** Customer records per level unless the level says otherwise. */
  records: 48,
  lostUpdateTrust: 3,
  /** A perturbation counts as handled once the rate is back to this share of what it was. */
  recoveryShare: 0.85,
} as const;

export interface TechDef {
  id: string;
  name: string;
  blurb: string;
  cost: number;
}

export const TECHS: TechDef[] = [
  { id: 'prompts1', name: 'Better prompts', blurb: 'Forges hallucinate a third less.', cost: 8 },
  { id: 'benchspeed', name: 'Parallel tests', blurb: 'Test benches run 50% faster.', cost: 10 },
  { id: 'belts', name: 'Belt overclock', blurb: 'All belts move 50% faster.', cost: 12 },
  { id: 'canary', name: 'Canary users', blurb: 'Flawed releases cost half as much trust.', cost: 10 },
  { id: 'quarantine', name: 'Quarantine', blurb: 'Benches catch corrupted work instead of catching the corruption.', cost: 14 },
  { id: 'autoreset', name: 'Self-healing breakers', blurb: 'Breakers reset themselves after a trip.', cost: 8 },
  { id: 'racks', name: 'Efficient racks', blurb: 'Racks supply 50% more compute.', cost: 12 },
  { id: 'refactor', name: 'Refactoring', blurb: 'Forges create 40% less tech debt.', cost: 12 },
  { id: 'prompts2', name: 'Evals', blurb: 'Forges hallucinate half as often again.', cost: 20 },
  { id: 'swarm', name: 'Drone swarm', blurb: 'Drone hubs launch twice as many drones.', cost: 16 },
  { id: 'range', name: 'Long-range monitors', blurb: 'Monitors reach 40% farther.', cost: 14 },
];

export function techById(id: string): TechDef | undefined {
  return TECHS.find((t) => t.id === id);
}
